import Button from 'components/common/Button'
import FileUploader from 'components/common/FileUploader'
import Modal from 'components/common/modal/Modal'
import {
  SecurityVerificationFiles,
  SecurityVerificationMode,
} from 'components/verification/verificationUtils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayMode } from 'types/customParams'

type SecurityVerificationModalProps = {
  isOpen: boolean
  mode: SecurityVerificationMode
  isLoading?: boolean
  onClose: () => void
  onSubmit: (files: SecurityVerificationFiles) => void
}

const emptyFiles = (): SecurityVerificationFiles => ({
  leaseAgreement: [],
  personalId: [],
  selfieWithId: [],
  proofOfIncome: [],
})

const SecurityVerificationModal = ({
  isOpen,
  mode,
  isLoading = false,
  onClose,
  onSubmit,
}: SecurityVerificationModalProps) => {
  const { t } = useTranslation()
  const [uploadedFiles, setUploadedFiles] = useState<SecurityVerificationFiles>(emptyFiles())

  useEffect(() => {
    if (!isOpen) {
      setUploadedFiles(emptyFiles())
    }
  }, [isOpen])

  const handleUpload = (
    url: string,
    field: keyof SecurityVerificationFiles
  ) => {
    setUploadedFiles(prev => ({
      ...prev,
      [field]: [...(prev[field] ?? []), url],
    }))
  }

  const canSubmit = (() => {
    if (mode === 'owner-subsequent') {
      return (uploadedFiles.leaseAgreement?.length ?? 0) > 0
    }
    if (mode === 'owner-initial') {
      return (
        (uploadedFiles.leaseAgreement?.length ?? 0) > 0 &&
        (uploadedFiles.personalId?.length ?? 0) > 0 &&
        (uploadedFiles.selfieWithId?.length ?? 0) > 0
      )
    }
    return (
      (uploadedFiles.personalId?.length ?? 0) > 0 &&
      (uploadedFiles.proofOfIncome?.length ?? 0) > 0 &&
      (uploadedFiles.selfieWithId?.length ?? 0) > 0
    )
  })()

  const titleKey =
    mode === 'tenant'
      ? 'verification.tenantModalTitle'
      : 'verification.ownerModalTitle'

  const bodyKey =
    mode === 'tenant'
      ? 'verification.tenantModalBody'
      : mode === 'owner-initial'
        ? 'verification.ownerInitialModalBody'
        : 'verification.ownerSubsequentModalBody'

  return (
    <Modal
      manualSize={DisplayMode.Modal}
      title={t(titleKey)}
      isOpen={isOpen}
      onClose={onClose}
      headerVariant="light"
      className="!w-[min(92vw,920px)] !h-auto !max-h-[88vh] rounded-2xl"
      customHeaderClasses="!items-start !px-5 !py-4"
      customBodyClasses="!bg-white"
    >
      <div className="p-4 md:p-5">
        <div className="mb-5 text-sm text-gray-600">{t(bodyKey)}</div>

        <div className="space-y-4">
          {(mode === 'owner-initial' || mode === 'owner-subsequent') && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <FileUploader
                label={t('listing.leaseAgreement')}
                name="leaseAgreement"
                allowPdf
                onUploadSuccess={files => handleUpload(files[0], 'leaseAgreement')}
                multiple={false}
              />
            </div>
          )}

          {(mode === 'owner-initial' || mode === 'tenant') && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <FileUploader
                label={t('listing.personalIdDocument')}
                name="personalId"
                allowPdf
                onUploadSuccess={files => handleUpload(files[0], 'personalId')}
                multiple={false}
              />
            </div>
          )}

          {mode === 'tenant' && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <FileUploader
                label={t('verification.proofOfIncome')}
                name="proofOfIncome"
                allowPdf
                onUploadSuccess={files => handleUpload(files[0], 'proofOfIncome')}
                multiple={false}
              />
            </div>
          )}

          {(mode === 'owner-initial' || mode === 'tenant') && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <FileUploader
                label={t('verification.selfieWithId')}
                name="selfieWithId"
                onUploadSuccess={files => handleUpload(files[0], 'selfieWithId')}
                multiple={false}
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t('common.cancel')}
          </button>
          <Button
            isLoading={isLoading}
            onClick={() => onSubmit(uploadedFiles)}
            disabled={!canSubmit || isLoading}
            customStyle="!m-0 text-sm px-4 py-2"
          >
            {t('verification.submitForReview')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default SecurityVerificationModal
